import { supabase } from '../lib/supabase';

/**
 * Pobiera wszystkie powiadomienia użytkownika
 * @param {string} userId - ID użytkownika
 * @param {Object} options - Opcje filtrowania
 * @param {boolean} options.read - Filtruj po statusie przeczytania (true/false/null dla wszystkich)
 * @param {number} options.limit - Maksymalna liczba powiadomień
 * @param {number} options.page - Numer strony (1-based)
 * @param {number} options.pageSize - Liczba elementów na stronę
 * @param {string[]|null} options.types - Opcjonalna lista `type` (Supabase `.in`)
 * @returns {Promise<{data: Array, error: Error|null, total?: number}>}
 */
export async function getUserNotifications(userId, options = {}) {
  try {
    const { read = null, limit = null, page = null, pageSize = 20, types = null } = options;

    // Build base query for counting
    let countQuery = supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (read !== null) {
      countQuery = countQuery.eq('read', read);
    }

    if (Array.isArray(types) && types.length > 0) {
      countQuery = countQuery.in('type', types);
    }

    // Get total count if pagination is used
    let totalCount = null;
    if (page !== null) {
      const { count, error: countError } = await countQuery;
      if (countError) {
        console.error('Error counting notifications:', countError);
      } else {
        totalCount = count || 0;
      }
    }

    // Build main query
    let query = supabase
      .from('notifications')
      .select(`
        *,
        event:events(
          id,
          title,
          host_id
        ),
        application:event_applications(
          id,
          status,
          event_id
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (read !== null) {
      query = query.eq('read', read);
    }

    if (Array.isArray(types) && types.length > 0) {
      query = query.in('type', types);
    }

    // Apply pagination or limit
    if (page !== null && pageSize) {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);
    } else if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching notifications:', error);
      return { data: null, error, total: totalCount };
    }

    return { data: data || [], error: null, total: totalCount };
  } catch (error) {
    console.error('Error in getUserNotifications:', error);
    return { data: null, error };
  }
}

/**
 * Pobiera liczbę nieprzeczytanych powiadomień
 * @param {string} userId - ID użytkownika
 * @returns {Promise<{count: number, error: Error|null}>}
 */
export async function getUnreadNotificationsCount(userId) {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) {
      console.error('Error fetching unread notifications count:', error);
      return { count: 0, error };
    }

    return { count: count || 0, error: null };
  } catch (error) {
    console.error('Error in getUnreadNotificationsCount:', error);
    return { count: 0, error };
  }
}

/**
 * Oznacza powiadomienie jako przeczytane
 * @param {string} notificationId - ID powiadomienia
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export async function markNotificationAsRead(notificationId) {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in markNotificationAsRead:', error);
    return { success: false, error };
  }
}

/**
 * Oznacza wszystkie powiadomienia użytkownika jako przeczytane
 * @param {string} userId - ID użytkownika
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export async function markAllNotificationsAsRead(userId) {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in markAllNotificationsAsRead:', error);
    return { success: false, error };
  }
}

/**
 * Usuwa powiadomienie
 * @param {string} notificationId - ID powiadomienia
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export async function deleteNotification(notificationId) {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in deleteNotification:', error);
    return { success: false, error };
  }
}

/**
 * Usuwa wszystkie powiadomienia użytkownika
 * @param {string} userId - ID użytkownika
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export async function deleteAllNotifications(userId) {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting all notifications:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in deleteAllNotifications:', error);
    return { success: false, error };
  }
}

/**
 * Formatuje tekst powiadomienia na podstawie typu
 * @param {Object} notification - Obiekt powiadomienia
 * @param {Object} t - Funkcja tłumaczenia z i18next
 * @returns {string} - Sformatowany tekst powiadomienia
 */
export function formatNotificationText(notification, t) {
  const { type, data, event } = notification;

  switch (type) {
    case 'application_received':
      return t('notifications.types.application_received', { 
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'application_accepted':
      return t('notifications.types.application_accepted', { 
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'application_rejected':
      return t('notifications.types.application_rejected', { 
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'event_created':
      return t('notifications.types.event_created', { 
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'event_starts_soon':
      return t('notifications.types.event_starts_soon', { 
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'event_started':
      return t('notifications.types.event_started', { 
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'event_cancelled':
      return t('notifications.types.event_cancelled', { 
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'event_invitation':
      return t('notifications.types.event_invitation', { 
        inviterName: data?.inviter_name || t('notifications.unknown_user'),
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'friend_invitation':
      return t('notifications.types.friend_invitation', { 
        senderName: data?.sender_name || t('notifications.unknown_user')
      });
    case 'friendship_accepted':
      return t('notifications.types.friendship_accepted', { 
        friendName: data?.friend_name || t('notifications.unknown_user')
      });
    case 'friend_joined_event':
      return t('notifications.types.friend_joined_event', { 
        friendName: data?.friend_name || t('notifications.unknown_user'),
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event')
      });
    case 'participant_kicked':
      return t('notifications.types.participant_kicked', {
        eventTitle: event?.title || data?.event_title || t('notifications.unknown_event'),
        kickerName: data?.kicker_name || t('notifications.unknown_user'),
      });
    case 'badge_unlocked':
      return t('notifications.types.badge_unlocked', {
        badgeName: data?.badge_label || data?.badge_id || t('notifications.unknown_badge'),
      });
    default:
      return t('notifications.types.unknown');
  }
}

